import Button from '@repo/ui/button'
import {
  DataTableSelectedData,
  DataTableSelectType,
  PaginationRequest,
  PaginationResponse,
  TableRequiredFields
} from '@repo/ui/data-table'
import Typography from '@repo/ui/typography'
import React, { useEffect, useState } from 'react'

import { restApiClient } from '../../../api/rest-api-client'
import { encodeNestedObject } from '../../../lib/utils/functions/misc.functions'
import DynamicDataTable, { DynamicDataTableProps, DynamicTableRef } from '../dynamic-table'

type Props<
  TData,
  TFilter,
  TExpanded extends TableRequiredFields | undefined = undefined
> = DynamicDataTableProps<TData, TFilter, TExpanded> & {
  isLoading?: boolean
  confirmationText?: string
  selectionCountLabel?: (count: number) => string
  selectedRows?: DataTableSelectType<TData>
  onCancel: () => void
  onChange?: (data: TData[]) => void
  onSelect?: (data: TData[]) => void
}

const SearchableDataTable = <
  TData extends TableRequiredFields,
  TFilter,
  TExpanded extends TableRequiredFields | undefined = undefined
>({
  isLoading,
  selectedRows: initialSelectedRows = [],
  confirmationText = 'Valider',
  selectionCountLabel,
  onChange,
  onCancel,
  onSelect,
  ...props
}: Props<TData, TFilter, TExpanded>) => {
  const [selectedData, setSelectedData] = useState<DataTableSelectedData<TData, TExpanded>>({
    expandedRows: [],
    isAllSelected: initialSelectedRows === 'ALL',
    rows: initialSelectedRows === 'ALL' ? [] : initialSelectedRows
  })
  const [search, setSearch] = useState('')
  const tableRef = React.useRef<DynamicTableRef<TData>>(null)

  const fetchData = async (fn: (data: TData[]) => void) => {
    if (selectedData.isAllSelected) {
      tableRef.current?.startSelectAllLoading()
      const allData = await restApiClient
        .url(encodeNestedObject<PaginationRequest>(props.endpoint, { search, version: 'LIGHT' }))
        .get<PaginationResponse<TData>>()
      fn(allData.data)
      tableRef.current?.endSelectAllLoading()
    } else fn(selectedData.rows)
  }

  useEffect(() => {
    if (onChange) fetchData(onChange)
  }, [selectedData])

  return (
    <div>
      <DynamicDataTable
        ref={tableRef}
        {...props}
        selectable
        permissions={{ delete: false }}
        headerType="light"
        selectedData={selectedData}
        onSelectedDataChange={setSelectedData}
        onSearchChange={setSearch}
      />

      {onSelect && (
        <div className="flex flex-row justify-end items-center mt-5 gap-3 border-t border-t-white-active p-5">
          {selectionCountLabel && (
            <Typography variant="secondary-body" className="mr-auto">
              {selectionCountLabel(selectedData.rows.length)}
            </Typography>
          )}
          <Button
            disabled={isLoading}
            variant="ghost"
            type="button"
            label="Fermer"
            aria-label={'Fermer'}
            onClick={() => {
              setSelectedData({
                isAllSelected: false,
                rows: [],
                expandedRows: []
              })
              onCancel()
            }}
          />
          <Button
            loading={isLoading}
            type={'button'}
            disabled={selectedData.rows.length === 0}
            variant="primary"
            label={confirmationText}
            aria-label={confirmationText}
            onClick={async () => {
              await fetchData(onSelect)
              setSelectedData({
                isAllSelected: false,
                rows: [],
                expandedRows: []
              })
            }}
          />
        </div>
      )}
    </div>
  )
}

export default SearchableDataTable
